import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  PartnersSelectionItemWithType,
  LoadingFacade,
  PartnerBrief,
  PartnerListFacade,
  PartnerListSearchCriteria,
  PartnerType,
  PartnerExtIdsModel,
  PartnerServiceType,
} from '../../../store';
import {
  ListQueryParam,
  ListRange,
  ListServerPagination,
  ListServerPaginationStateModel,
  unchangedForm,
} from '../../../common';
import { ButtonAction, ButtonType } from '../../../ui-kit/action-button';
import { FilterSearch, FilterSelect, FilterSettings, SelectOption } from '../../../ui-kit/list-filter';
import { TranslateService } from '@ngx-translate/core';
import { PartnersSelectionItem } from '../models';

interface DialogDataModel {
  ownerList: PartnersSelectionItem[];
  predefinedType: PartnerType;
}

@Component({
    selector: 'lib-partners-selection-dialog',
    templateUrl: './partners-selection-dialog.component.html',
    styleUrls: ['./partners-selection-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PartnersSelectionDialogComponent
  extends ListServerPagination<PartnerBrief>
  implements OnInit, AfterViewInit, OnDestroy
{
  displayedColumns = ['select', 'name', 'type1', 'id1', 'type2', 'id2'];
  listDto$: Observable<ListServerPaginationStateModel<PartnerBrief>>;
  selection: SelectionModel<PartnerBrief> = new SelectionModel<PartnerBrief>(true, []);
  filterConfig: FilterSettings;
  searchCriteriaValue: PartnerListSearchCriteria = new PartnerListSearchCriteria({ [ListQueryParam.Active]: true });
  buttonAction = ButtonAction;
  buttonType = ButtonType;
  predefinedType: PartnerType;
  partnerBriefList: PartnerBrief[] = [];
  type1Index: number;
  type2Index: number;
  initialPartnerList: number[] = [];
  isFormUnchanged: (intialPartnerValue: number[], updatedPartnerList: number[]) => boolean = unchangedForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    translateService: TranslateService,
    public dialogRef: MatDialogRef<PartnersSelectionDialogComponent>,
    public partnerListFacade: PartnerListFacade,
    public loadingFacade: LoadingFacade,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataModel,
  ) {
    super(translateService);
    this.listDto$ = partnerListFacade.listDto$;
    this.predefinedType = data?.predefinedType;
    this.initialFetch = !this.predefinedType;
  }

  ngOnInit(): void {
    this.initFiltersForm();
    if (this.data && this.data.ownerList) {
      this.data.ownerList.forEach((row: PartnersSelectionItemWithType) => {
        const partnerBrief = new PartnerBrief();
        partnerBrief.id = row.id;
        partnerBrief.name = row.name;
        partnerBrief.type = row.type;
        partnerBrief.extIds = row?.extIds;
        this.partnerBriefList.push(partnerBrief);
        this.initialPartnerList.push(row.id);
      });
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (this.predefinedType) {
      setTimeout(
        () =>
          this.fetchListBySearchCriteria(
            new PartnerListSearchCriteria({
              partnerType: this.predefinedType,
              name: '',
            }),
          ),
        0,
      );
    }
  }

  ngOnDestroy(): void {
    this.partnerListFacade.setInitialState();
  }

  updatedPartnerList(): number[] {
    return this.partnerBriefList.map((partner: PartnerBrief) => partner.id);
  }

  fetchList(listRange: ListRange): void {
    timer(0)
      .pipe(take(1))
      .subscribe(() => {
        this.searchCriteriaValue = {
          ...this.searchCriteriaValue,
          ...listRange,
        };
        this.partnerListFacade.loadListByFilter(this.searchCriteriaValue);
      });
  }

  fetchListBySearchCriteria(filterValue: PartnerListSearchCriteria): void {
    this.searchCriteriaValue = {
      ...this.searchCriteriaValue,
      ...filterValue,
      page: 0,
    };
    this.partnerListFacade.loadListByFilter(this.searchCriteriaValue);
  }

  noPartnersExist(): boolean {
    const isSearchCriteriaInitialValue =
      !this.searchCriteriaValue[ListQueryParam.Name] && !this.searchCriteriaValue[ListQueryParam.PartnerTypeFilter];

    return isSearchCriteriaInitialValue && this.dataSource && this.dataSource.data.length === 0;
  }

  confirm(): void {
    this.dialogRef.close(this.partnerBriefList);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected() ? this.removeData() : this.addData();
  }

  removeData(): void {
    this.dataSource.data.forEach((row: PartnerBrief) => {
      this.partnerBriefList.forEach((partner: PartnerBrief, index: number) => {
        if (partner.id === row.id) {
          this.partnerBriefList.splice(index, 1);
        }
      });
    });
    this.selection.clear();
  }

  addData(): void {
    this.dataSource.data.forEach((row: PartnerBrief) => {
      this.partnerBriefList.forEach((partner: PartnerBrief, _index: number) => {
        if (partner.id === row.id) {
          this.partnerBriefList = this.partnerBriefList.filter((partnerElement: PartnerBrief) => {
            return partnerElement.id !== row.id;
          });
        }
      });
    });
    this.dataSource.data.forEach((row: PartnerBrief) => {
      this.selection.select(row);
      this.partnerBriefList.push(row);
    });
  }

  findType1Index(extIds: PartnerExtIdsModel[]): number {
    this.type1Index = extIds.findIndex((extId: PartnerExtIdsModel) => {
      return extId.type === PartnerServiceType.Tms || extId.type === PartnerServiceType.ShipTo;
    });
    return this.type1Index;
  }

  findType2Index(extIds: PartnerExtIdsModel[]): number {
    this.type2Index = extIds.findIndex((extId: PartnerExtIdsModel) => {
      return extId.type === PartnerServiceType.Sap || extId.type === PartnerServiceType.SoldTo;
    });
    return this.type2Index;
  }

  protected updateTable(tableDto: ListServerPaginationStateModel<PartnerBrief>): void {
    super.updateTable(tableDto);
    this.selection.clear();
    this.dataSource.data.forEach((row: PartnerBrief) => {
      if (this.partnerBriefList && this.partnerBriefList.some((partner: PartnerBrief) => row.id === partner.id)) {
        this.selection.select(row);
      }
    });
  }

  public addOrRemoveRow(row: PartnerBrief, shouldAdd: boolean): void {
    const index = this.findIndexById(this.partnerBriefList, row.id);
    if (shouldAdd) {
      this.partnerBriefList.push(row);
    } else {
      this.partnerBriefList.splice(index, 1);
    }
  }

  private findIndexById(array: PartnerBrief[], id: number): number {
    return array.findIndex((item: PartnerBrief) => item.id === id);
  }

  private initFiltersForm(): void {
    this.filterConfig = {
      items: [
        new FilterSearch({ name: ListQueryParam.Name }),
        new FilterSelect({
          name: ListQueryParam.Types,
          isMultiple: true,
          initialValue: [this.predefinedType],
          disabled: !!this.predefinedType,
          options: Object.values(PartnerType).map(
            (option: string) =>
              new SelectOption({
                name: 'common.filter.partnerType.option.' + option.toLowerCase(),
                value: option,
              }),
          ),
        }),
      ],
    };
  }
}
